/* eslint-disable @typescript-eslint/no-explicit-any */
import { getAgeFromDate } from '@catalyze/ui';
import type { TExpectedDecisionMaker } from 'api/types/edm';
import { APIColumns, Columns, HiddenColumns } from 'constants/columns';

import type { prepareData } from './prepareData';

export const prepareIterableData = <TData = unknown>(data: TData[]) => {
    return data?.reduce<{ [key: string]: unknown }>(
        (acc, item) => Object.assign(acc, item),
        {}
    );
};

export const prepareEDMsEmails = (
    edmIndex: number,
    emails: TExpectedDecisionMaker['emails']
) => {
    const preparedEmails = emails?.map((email, index: number) => {
        const currentIndex = index + 1; // start counting from 1...
        return {
            [`EDM ${edmIndex} Email ${currentIndex}`]: email.value,
        };
    });
    return prepareIterableData(preparedEmails);
};

export const prepareEDMsPhones = (
    edmIndex: number,
    phones: TExpectedDecisionMaker['phones'],
    showDnc: boolean | undefined
) => {
    const preparedPhones = phones?.map((phone, index: number) => {
        const currentIndex = index + 1; // start counting from 1...
        if (!showDnc) {
            return {
                [`EDM ${edmIndex} Phone ${currentIndex}`]: phone.number,
            };
        }
        return {
            [`EDM ${edmIndex} Phone ${currentIndex}`]: phone.number,
            [`EDM ${edmIndex} Phone ${currentIndex} is on the DNC list`]:
                phone.dnc,
        };
    });

    return prepareIterableData(preparedPhones);
};

export const prepareEDMsAddresses = (
    edmIndex: number,
    addresses: TExpectedDecisionMaker['addresses']
) => {
    const preparedAddresses = addresses?.map((address, index: number) => {
        const currentIndex = index + 1; // start counting from 1...
        return {
            [`EDM ${edmIndex} Street ${currentIndex}`]: address.address,
            [`EDM ${edmIndex} City ${currentIndex}`]: address.city,
            [`EDM ${edmIndex} State ${currentIndex}`]: address.state,
            [`EDM ${edmIndex} Zip ${currentIndex}`]: address.zip,
        };
    });

    return prepareIterableData(preparedAddresses);
};

export const prepareEDMs = (
    edms: TExpectedDecisionMaker[],
    showDnc: boolean | undefined
) => {
    const formattedEdms = edms.map((edm, index: number) => {
        const currentIndex = index + 1; // start counting from 1...
        const addresses = prepareEDMsAddresses(currentIndex, edm.addresses);
        const phones = prepareEDMsPhones(currentIndex, edm.phones, showDnc);
        const emails = prepareEDMsEmails(currentIndex, edm.emails);

        return {
            [`EDM ${currentIndex} First name`]: edm.firstName,
            [`EDM ${currentIndex} Last name`]: edm.lastName,
            [`EDM ${currentIndex} Age`]: getAgeFromDate(edm.dateOfBirth),
            [`EDM ${currentIndex} Relationship`]: edm.relationship,
            ...addresses,
            ...phones,
            ...emails,
        };
    });

    return prepareIterableData(formattedEdms);
};

export const prepareExportData = (
    dataToExport: ReturnType<typeof prepareData>,
    showDnc: boolean | undefined
) =>
    dataToExport.map((row: any) => {
        const edms = prepareEDMs(row[HiddenColumns.EDMS], showDnc);
        return {
            'Property ID': row[Columns.ID],
            'Property Owner 1': row[HiddenColumns.OWNER1][0],
            'Property Owner 2': row[HiddenColumns.OWNER2][1],
            'Property Sold': row[APIColumns.IS_LISTED],
            'Property Address': row[Columns.ADDRESS].replace(/^🚩 /u, ''),
            'Property City': row[Columns.CITY],
            'Property State': row[Columns.STATE],
            'Property Zip': row[Columns.ZIP],
            'Property Value': row[Columns.MARKET_VALUE],
            Scenario: row[Columns.LEAD_TYPE],
            'Estate First Name': row[HiddenColumns.OBIT_FIRST_NAME],
            'Estate Last Name': row[HiddenColumns.OBIT_LAST_NAME],
            'Date Added': row[Columns.DATE_ADDED],
            ...edms,
        };
    });

export const prepareSelectAllExportData = (
    dataToExport: ReturnType<typeof prepareData>,
    showDnc: boolean | undefined
) => {
    return dataToExport.map((row: any) => {
        const edms = prepareEDMs(row[APIColumns.EDMS], showDnc);
        return {
            'Property ID': row[APIColumns.ID],
            'Property Owner 1': row[APIColumns.OWNERS][0],
            'Property Owner 2': row[APIColumns.OWNERS][1],
            'Property Sold': row[APIColumns.IS_LISTED],
            'Property Address': row[APIColumns.ADDRESS].replace(/^🚩 /u, ''),
            'Property City': row[APIColumns.CITY],
            'Property State': row[APIColumns.STATE],
            'Property Zip': row[APIColumns.ZIP],
            'Property Value': row[APIColumns.MARKET_VALUE],
            Scenario: row[APIColumns.LEAD_TYPE],
            'Estate First Name': row[APIColumns.OBIT_FIRST_NAME],
            'Estate Last Name': row[APIColumns.OBIT_LAST_NAME],
            'Date Added': row[APIColumns.DATE_ADDED],

            ...edms,
        };
    });
};
